import { JLPTSection } from './types';
import type { SectionIntro } from '../../sectionIntro';

export type { SectionIntro };

/**
 * JLPT 语言知识（文字·词汇）介绍
 */
const JLPT_VOCABULARY_INTRO: SectionIntro = {
  sectionId: JLPTSection.VOCABULARY,
  title: '语言知识·文字词汇 (言語知識・文字・語彙)',
  description: 'JLPT 文字·词汇部分测试您对日语汉字读音、汉字书写、词汇构成和词汇用法的掌握程度。题型包括汉字读法、表记（根据读音选汉字）、词语构成、根据语境选择恰当的词语、近义词替换和词汇用法等。题数和时长随等级（N5-N1）不同而变化，级别越高词汇量要求越大。',
  duration: 25, // 与 sections.ts 中一致（N2 级别参考时长）
  tips: [
    '重点复习考级大纲范围内的汉字和词汇，按等级分层记忆',
    '汉字读法题注意音读和训读的区别，以及多音字',
    '语境选择题先读完整句，把握句子整体意思再选词',
    '近义词题要理解词汇的细微差别和使用场合',
    '词汇用法题注意词性搭配和惯用表达',
    '平时多积累副词、外来语和惯用语，这些是高频考点',
    '合理安排时间，遇到不会的题先标记，做完后再回头检查',
  ],
};

/**
 * JLPT 语言知识（语法）·阅读介绍
 */
const JLPT_GRAMMAR_READING_INTRO: SectionIntro = {
  sectionId: JLPTSection.GRAMMAR_READING,
  title: '语言知识·语法与阅读 (言語知識・文法・読解)',
  description: 'JLPT 语法·阅读部分分为语法和读解两大块。语法题包括语法形式选择、句子排序组合和文章语法（完形填空）；读解题包括短篇、中篇和长篇阅读，内容涵盖评论、说明文、广告、通知、书信等多种文体，考查信息检索、主旨理解和细节把握能力。',
  duration: 105, // 与 sections.ts 中一致（N2 级别参考时长）
  tips: [
    '语法选择注意句型接续（前面接什么词形）和语义是否自然',
    '句子排序题先找首尾句，再利用接续词和指代词确定中间顺序',
    '文章语法（完形填空）要通读全文，结合上下文逻辑选择',
    '短篇阅读先看问题再读文章，快速定位答案',
    '中长篇阅读先把握文章结构和作者观点，再处理细节题',
    '注意转折词（しかし、でも、ところが）后往往是作者真正观点',
    '读解题分值占比高，建议给阅读留足时间',
    '长篇阅读不要逐字翻译，训练抓关键词和段落大意的速读能力',
  ],
};

/**
 * JLPT 听力介绍
 */
const JLPT_LISTENING_INTRO: SectionIntro = {
  sectionId: JLPTSection.LISTENING,
  title: '听力 (聴解)',
  description: 'JLPT 听力部分测试您在日常生活和学习场景中对日语口语的理解能力。题型包括课题理解（听懂任务和行动指示）、要点理解（抓住对话要点）、概要理解（把握独白整体内容）、语言表达（选择合适的说法）、即时应答（对短句快速反应）和综合理解（比较长篇对话，高级别）。音频只播放一遍。',
  duration: 50, // 与 sections.ts 中一致（N2 级别参考时长）
  tips: [
    '音频只播放一遍，集中注意力，利用播放前的间隙快速读题',
    '课题理解重点听"首先要做什么"，注意动作的顺序词（まず、それから）',
    '要点理解带着问题听，抓住与问题相关的关键信息',
    '概要理解先听整体内容，把握说话者的主张和话题',
    '即时应答考查口语惯用表达，注意语气和场景是否匹配',
    '综合理解（N1/N2）信息量较大，建议边听边做简单笔记',
    '没听清的部分不要纠结，立即准备下一题',
    '平时多听日语广播、新闻和会话材料，习惯自然语速',
  ],
};

/**
 * JLPT Section 介绍文案映射
 */
export const JLPT_SECTION_INTROS: Record<string, SectionIntro> = {
  [JLPTSection.VOCABULARY]: JLPT_VOCABULARY_INTRO,
  [JLPTSection.GRAMMAR_READING]: JLPT_GRAMMAR_READING_INTRO,
  [JLPTSection.LISTENING]: JLPT_LISTENING_INTRO,
};

/**
 * 获取 JLPT Section 介绍文案
 */
export function getJLPTSectionIntro(sectionId: string): SectionIntro | null {
  return JLPT_SECTION_INTROS[sectionId] || null;
}
